import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Copy, Check, Trash2, CheckCircle2 } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { PageSkeleton } from '@/components/shared/page-skeleton'
import { toast } from 'sonner'
import { useSettings, QUERY_KEYS } from '@/hooks'
import { api, type MerchantSettings } from '@/lib/api'
import { SectionCard } from '@/components/shared/section-card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { GuruSyncCard } from '@/components/settings/guru-sync-card'
import { EventsTimeline } from '@/components/settings/events-timeline'

export const Route = createFileRoute('/_app/settings/integrations/guru')({
  component: GuruSettingsPage,
})

function GuruSettingsPage() {
  const { data: settings, isLoading } = useSettings()

  if (isLoading) {
    return <PageSkeleton sections={2} />
  }

  if (!settings) return null

  return (
    <div className="space-y-6">
      <PageHeader
        size="sm"
        title="Guru"
        description="Receba vendas e processe comissões automaticamente."
        backHref="/settings/integrations"
      />

      <GuruConfigSection settings={settings} />

      {settings.hasGuruToken && (
        <>
          <GuruSyncCard merchantId={settings.id} />

          <Separator />

          <div className="space-y-4">
            <div>
              <h4 className="text-base font-medium">Últimos eventos do Guru</h4>
              <p className="text-sm text-muted-foreground">Eventos recebidos desta integração.</p>
            </div>
            <EventsTimeline
              filter={{ platform: 'guru', direction: 'in' }}
              limit={10}
              showFilters={false}
            />
          </div>
        </>
      )}
    </div>
  )
}

function GuruConfigSection({ settings }: { settings: MerchantSettings }) {
  const queryClient = useQueryClient()
  const [guruToken, setGuruToken] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const updateIntegrations = useMutation({
    mutationFn: (data: { guruApiToken?: string }) => api.settings.updateIntegrations(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings })
      setGuruToken('')
      toast.success('Integração configurada com sucesso!')
    },
    onError: () => {
      toast.error('Erro ao salvar configuração')
    },
  })

  const removeIntegration = useMutation({
    mutationFn: () => api.settings.removeIntegration('guru'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings })
      toast.success('Integração removida')
    },
  })

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <SectionCard title="Configuração" description="Conecte sua conta da Guru ao Rafobase.">
      <div className="space-y-6">
        {/* Webhook URL */}
        <div className="space-y-2">
          <Label>URL do Webhook (cole na Guru)</Label>
          <div className="flex gap-2">
            <Input value={settings.webhookUrl} readOnly className="font-mono text-xs" />
            <ChonkyButton
              variant="secondary"
              size="icon"
              onClick={() => copyToClipboard(settings.webhookUrl, 'guru-webhook')}
            >
              {copiedId === 'guru-webhook' ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </ChonkyButton>
          </div>
          <p className="text-xs text-muted-foreground">
            Configure esta URL em Configurações → Webhooks no painel da Guru.
          </p>
        </div>

        {/* Token */}
        <div className="space-y-2">
          <Label>Token da API</Label>
          {settings.hasGuruToken ? (
            <div className="flex items-center justify-between p-3 border rounded-md bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  Conectado
                </span>
              </div>
              <ChonkyButton
                variant="destructive"
                size="sm"
                onClick={() => removeIntegration.mutate()}
                loading={removeIntegration.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Desconectar
              </ChonkyButton>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder="Cole o token da API Guru..."
                value={guruToken}
                onChange={(e) => setGuruToken(e.target.value)}
              />
              <ChonkyButton
                onClick={() => updateIntegrations.mutate({ guruApiToken: guruToken })}
                disabled={!guruToken}
                loading={updateIntegrations.isPending}
              >
                Conectar
              </ChonkyButton>
            </div>
          )}
          {!settings.hasGuruToken && (
            <p className="text-xs text-muted-foreground">
              Encontre em Configurações → Dados de Acesso (API) no painel da Guru.
            </p>
          )}
        </div>
      </div>
    </SectionCard>
  )
}
